import React, { useState, useEffect } from "react";
import {
  MessageCircle,
  PhoneCall,
  Mail,
  Plus,
  Search,
  MessageSquare,
  Phone,
  Send,
  Clock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import "./Support.scss";
import "../features/support/styles/chatwoot.scss";
import Header from "../components/layout/Header";
import { useChatwoot } from "../features/support/ChatwootProvider";
import {
  createConversation,
  sendMessage,
  getLastMessage,
  formatMessageTime,
  getUnreadCount,
  getDashboardStats,
  DashboardStats,
  setCurrentContact,
} from "../services/chatwootService";

interface HelpOption {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ size?: number }>;
  action: () => void;
}

const Support: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"conversations" | "help">("help");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [newMessageContent, setNewMessageContent] = useState("");
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats>({
    total: 0,
    open: 0,
    resolved: 0,
    unread: 0,
    recentActivity: null,
  });
  const navigate = useNavigate();

  const {
    conversations,
    contact,
    totalUnread,
    isConnected,
    isLoading,
    error,
    refreshConversations,
  } = useChatwoot();

  // Update contact in global state when available
  React.useEffect(() => {
    if (contact) {
      setCurrentContact(contact);
      console.log('Support: Contact set in global state:', contact.source_id);
    }
  }, [contact]);

  // Update dashboard stats
  React.useEffect(() => {
    const updateStats = async () => {
      const stats = await getDashboardStats();
      setDashboardStats(stats);
      console.log('Support: Dashboard stats updated', stats);
    };
    void updateStats();
  }, [conversations]);

  const handleRefresh = async () => {
    await refreshConversations();
    const stats = await getDashboardStats();
    setDashboardStats(stats);
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery) return true;
    const lastMsg = getLastMessage(conv);
    const messageContent = lastMsg?.content || "";
    return messageContent.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const openConversations = conversations.filter((c) => c.status === "open").length;
  const resolvedConversations = conversations.filter((c) => c.status === "resolved").length;

  const handleStartNewConversation = async () => {
    if (!newMessageContent.trim()) return;

    if (isLoading) {
      alert('Please wait while we initialize your chat connection...');
      console.log('[Support] Cannot create conversation: Still initializing', { isLoading });
      return;
    }
    
    if (!contact?.source_id) {
      alert('Contact information is not yet available. Please wait a moment and try again.');
      console.error('[Support] Cannot create conversation: Contact source_id not available', { contact, error });
      return;
    }

    if (error) {
      alert(`Failed to initialize chat: ${error}. Please refresh the page and try again.`);
      return;
    }

    setIsCreatingConversation(true);

    try {
      console.log('[Support] Creating new conversation with message:', newMessageContent.substring(0, 50));
      console.log('[Support] Using contact source_id:', contact.source_id);
      const convResult = await createConversation({}, contact.source_id);
      
      if (!convResult.success) {
        console.error('[Support] Failed to create conversation:', convResult.error);
        alert(`Failed to create conversation: ${convResult.error}`);
        return;
      }
      
      if (!convResult.data) {
        console.error('[Support] No conversation data returned');
        alert('Failed to create conversation: No data returned');
        return;
      }

      console.log('[Support] Conversation created:', convResult.data.id);
      
      const msgResult = await sendMessage(convResult.data.id, newMessageContent);
      
      if (!msgResult.success) {
        console.error('[Support] Failed to send message:', msgResult.error);
        alert(`Failed to send message: ${msgResult.error}`);
        return;
      }

      console.log('[Support] Message sent successfully');
      setNewMessageContent("");
      setShowNewConversationModal(false);
      await refreshConversations();
      navigate(`/support/chat/${convResult.data.id}`);
    } catch (error) {
      console.error("[Support] Failed to create conversation:", error);
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const helpOptions: HelpOption[] = [
    {
      title: "Chat support",
      subtitle: "Get instant support from our team",
      icon: MessageCircle,
      action: () => setActiveTab("conversations"),
    },
    {
      title: "Phone support",
      subtitle: "Call us at +91 96869 81633",
      icon: PhoneCall,
      action: () => {
        window.location.href = "tel:+919686981633";
      },
    },
    {
      title: "Email support",
      subtitle: "Email us at support@chargingadda.com",
      icon: Mail,
      action: () => {
        window.location.href = "mailto:support@chargingadda.com";
      },
    },
  ];

  return (
    <div className="support-container">
      <Sidebar className={isSidebarOpen ? "open" : ""} />
      {isSidebarOpen && (
        <div className="overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      <div className="support-content">
        <Header
          onMenuClick={() => setIsSidebarOpen((p) => !p)}
        />

        {/* Tabs */}
        <div className="support-tabs">
          <button
            className={`tab ${activeTab === "help" ? "active" : ""}`}
            onClick={() => setActiveTab("help")}
          >
            <Phone size={18} />
            Help
          </button>
          <button
            className={`tab ${activeTab === "conversations" ? "active" : ""}`}
            onClick={() => setActiveTab("conversations")}
          >
            <MessageSquare size={18} />
            Conversations ({conversations.length})
            {totalUnread > 0 && <span className="badge">{totalUnread}</span>}
          </button>
        </div>

        {/* Conversations Tab */}
        {activeTab === "conversations" && (
          <div className="conversations-section">
            {/* Stats */}
            <div className="conversations-stats">
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.total}</div>
                <div className="stat-label">Total Conversations</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.open}</div>
                <div className="stat-label">Open</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{dashboardStats.resolved}</div>
                <div className="stat-label">Resolved</div>
              </div>
            </div>

            {/* Search and Refresh */}
            <div className="conversations-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <button
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={!isConnected}
                title={isConnected ? "Refresh" : "Offline"}
              >
                🔄
              </button>
            </div>

            {/* Connection Status */}
            {!isConnected && (
              <div className="connection-warning">
                ⚠️ Offline mode - Changes will sync when connected
              </div>
            )}

            {/* Conversations List */}
            <div className="conversations-list">
              {filteredConversations.length === 0 ? (
                <div className="empty-state">
                  <MessageSquare size={48} />
                  <p>No conversations yet</p>
                  <button
                    className="start-chat-btn"
                    onClick={() => setShowNewConversationModal(true)}
                  >
                    <Plus size={18} />
                    Start New Conversation
                  </button>
                </div>
              ) : (
                filteredConversations.map((conversation) => {
                  const unreadCount = getUnreadCount(conversation);
                  const lastMessage = getLastMessage(conversation);

                  return (
                    <div
                      key={conversation.id}
                      className="conversation-item"
                      onClick={() => navigate(`/support/chat/${conversation.id}`)}
                    >
                      <div className="conversation-header">
                        <h4>Ticket #{conversation.id}</h4>
                        <span className={`status-badge ${conversation.status}`}>
                          {conversation.status}
                        </span>
                      </div>
                      <p className="conversation-preview">
                        {lastMessage?.content || "No messages yet"}
                      </p>
                      <div className="conversation-footer">
                        <span className="timestamp">
                          {lastMessage ? formatMessageTime(lastMessage.created_at) : ""}
                        </span>
                        {unreadCount > 0 && (
                          <span className="unread-badge">{unreadCount}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* FAB - New Conversation */}
            <button
              className="fab"
              onClick={() => setShowNewConversationModal(true)}
              title="Start new conversation"
            >
              <Plus size={24} />
            </button>
          </div>
        )}

        {/* Help Tab */}
        {activeTab === "help" && (
          <div className="help-section">
            {/* Hero Section */}
            <div className="support-hero">
              <div className="hero-icon-container">
                <MessageCircle size={48} />
              </div>
              <h1>How can we help you?</h1>
              <p>Start a conversation or browse support options</p>
              <div className="status-chip">
                <span className="status-dot">●</span>
                Support Available 24/7
              </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-section">
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon active">
                    <MessageCircle size={24} />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{conversations.filter(c => c.status === "open").length}</span>
                    <span className="stat-label">Active Chats</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon resolved">
                    <MessageSquare size={24} />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{resolvedConversations}</span>
                    <span className="stat-label">Resolved</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon unread">
                    <Mail size={24} />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">{totalUnread}</span>
                    <span className="stat-label">Unread</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions Section */}
            <div className="quick-actions-section">
              <h2 className="section-title">Get Support</h2>
              <div className="quick-actions-grid">
                {/* Start Chat */}
                <div
                  className="action-card primary"
                  onClick={() => setShowNewConversationModal(true)}
                >
                  <div className="action-icon-wrapper chat">
                    <Plus size={24} />
                  </div>
                  <h3>Start Chat</h3>
                  <p>Message our team</p>
                  <div className="action-badge">
                    <Clock size={14} />
                    <span>Avg. 2 min response</span>
                  </div>
                </div>

                {/* Email */}
                <div
                  className="action-card"
                  onClick={() => window.location.href = "mailto:support@chargingadda.com"}
                >
                  <div className="action-icon-wrapper email">
                    <Mail size={24} />
                  </div>
                  <h3>Email</h3>
                  <p className="contact-detail">support@chargingadda.com</p>
                  <p className="contact-subtitle">Send us an email</p>
                  <div className="action-badge">
                    <Clock size={14} />
                    <span>24 hrs response</span>
                  </div>
                </div>

                {/* Call Us */}
                <div
                  className="action-card"
                  onClick={() => window.location.href = "tel:+919686981633"}
                >
                  <div className="action-icon-wrapper call">
                    <PhoneCall size={24} />
                  </div>
                  <h3>Call Us</h3>
                  <p className="contact-detail">+91 96869 81633</p>
                  <p className="contact-subtitle">Speak directly</p>
                  <div className="action-badge">
                    <Clock size={14} />
                    <span>Available 24/7</span>
                  </div>
                </div>

                {/* WhatsApp */}
                <div
                  className="action-card"
                  onClick={() => window.location.href = "https://api.whatsapp.com/send/?phone=919686981633&text=Hi%2C+I+need+support.+My+name+is+USER+%287972826175%29&type=phone_number&app_absent=0"}
                >
                  <div className="action-icon-wrapper whatsapp">
                    <MessageCircle size={24} />
                  </div>
                  <h3>WhatsApp</h3>
                  <p className="contact-detail">+91 96869 81633</p>
                  <p className="contact-subtitle">Message us</p>
                  <div className="action-badge">
                    <Clock size={14} />
                    <span>Quick replies</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Hours Section */}
            <div className="contact-info-card">
              <h3>Hours of Operation</h3>
              <div className="contact-details">
                <div className="contact-item">
                  <Clock size={20} />
                  <div>
                    <strong>24/7 Support Available</strong>
                    <p>We're here to help anytime</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
              <button 
                className="start-chat-cta"
                onClick={() => {
                  setActiveTab("conversations");
                  setShowNewConversationModal(true);
                }}
              >
                <MessageCircle size={20} />
                Start Live Chat
                <MessageSquare size={18} />
              </button>
              <div className="login-footer">
                <a href="https://chargingadda.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="footer-link">
                  Privacy Policy
                </a>
                <span className="footer-separator">•</span>
                <a href="https://chargingadda.com/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="footer-link">
                  Terms of Service
                </a>
                <span className="footer-separator">•</span>
                <a href="https://chargingadda.com/return-policy" target="_blank" rel="noopener noreferrer" className="footer-link">
                  Refund Policy
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <div className="modal-overlay" onClick={() => setShowNewConversationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start New Conversation</h2>
              <button
                className="modal-close"
                onClick={() => setShowNewConversationModal(false)}
              >
                ✕
              </button>
            </div>

            {isLoading && (
              <div style={{
                padding: '12px',
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                ⏳ Initializing your chat connection... Please wait a moment.
              </div>
            )}

            {error && (
              <div style={{
                padding: '12px',
                backgroundColor: '#ffebee',
                color: '#c62828',
                borderRadius: '4px',
                marginBottom: '12px',
                fontSize: '14px'
              }}>
                ❌ {error}
              </div>
            )}

            <textarea
              placeholder="Type your message..."
              value={newMessageContent}
              onChange={(e) => setNewMessageContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.ctrlKey) {
                  handleStartNewConversation();
                }
              }}
              disabled={isLoading || error !== null}
            />
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => setShowNewConversationModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleStartNewConversation}
                disabled={isLoading || error !== null || isCreatingConversation || !newMessageContent.trim()}
              >
                {isLoading ? 'Initializing...' : isCreatingConversation ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
